import React, { createContext, useContext, useCallback, useEffect } from "react";

import { useLocation } from "wouter";
import { dictionaries, type Locale } from "../../../shared/translations";
import { localeForPath, translatedPath } from "../../../shared/localePaths";
export type { Locale } from "../../../shared/translations";

interface LocaleContextType {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: string) => string;
}

const LocaleContext = createContext<LocaleContextType | undefined>(undefined);

interface LocaleProviderProps {
  children: React.ReactNode;
}

export function LocaleProvider({ children }: LocaleProviderProps) {
  const [location, navigate] = useLocation();
  // URL is authoritative: browser settings and saved preferences must not
  // turn an English document into Spanish (or change its canonical URL).
  const locale = localeForPath(location);
  const setLocale = useCallback((newLocale: Locale) => {
    navigate(translatedPath(location, newLocale));
  }, [location, navigate]);

  const t = useCallback((key: string): string => {
    return dictionaries[locale][key] || dictionaries["es"][key] || key;
  }, [locale]);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  return (
    <LocaleContext.Provider value={{ locale, setLocale, t }}>
      {children}
    </LocaleContext.Provider>
  );
}

export function useLocale() {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error("useLocale must be used within LocaleProvider");
  }
  return context;
}
